import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@fluentui/react-components';
import { AsyncButton } from '../common/AsyncButton';
import { Alert } from '../common/Alert';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { LLMProviderForm } from './LLMProviderForm';
import type {
    SavedProvider,
    LLMProviderConfig
} from '../../types/llmSettings';
import { llmSettingsApi, type LLMTestRequest } from '../../services/llmSettingsApi';

interface LLMProviderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (config: LLMProviderConfig) => Promise<void>;
    mode: 'add' | 'edit';
    existingProvider?: SavedProvider;
    existingConfig?: LLMProviderConfig;
    isDuplicating?: boolean; // Indicates if this is a duplication operation
}

export const LLMProviderModal: React.FC<LLMProviderModalProps> = ({
    isOpen,
    onClose,
    onSave,
    mode,
    existingProvider: _existingProvider,
    existingConfig,
    isDuplicating = false
}) => {
    const [isValid, setIsValid] = useState(false);
    const [_formErrors, setFormErrors] = useState<string[]>([]);
    const [currentConfig, setCurrentConfig] = useState<Partial<LLMProviderConfig>>({});
    const [hasChanges, setHasChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [testError, setTestError] = useState<string | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isOpen) return;

            // Escape key to close
            if (event.key === 'Escape') {
                handleClose();
            }

            // Ctrl+S or Cmd+S to save
            if ((event.ctrlKey || event.metaKey) && event.key === 's') {
                event.preventDefault();
                if (isValid && !isSaving && !isTesting) {
                    handleSave();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isValid, isSaving, isTesting, hasChanges]);

    const handleFormValidationChange = (valid: boolean, errors: string[]) => {
        setIsValid(valid);
        setFormErrors(errors);
    };

    const handleFormChange = (config: Partial<LLMProviderConfig>, changed: boolean) => {
        setCurrentConfig(config);
        setHasChanges(changed);
    };

    const handleClose = () => {
        if (hasChanges && !isSaving && !isTesting) {
            setShowConfirmDialog(true);
        } else {
            onClose();
        }
    };

    const handleConfirmClose = () => {
        setShowConfirmDialog(false);
        onClose();
    };

    const handleCancelClose = () => {
        setShowConfirmDialog(false);
    };

    const handleSave = async () => {
        if (!isValid || isSaving || isTesting) return;

        // Clear any previous test errors
        setTestError(null);

        // Build final config
        const finalConfig: LLMProviderConfig = {
            ...currentConfig
        } as LLMProviderConfig;

        // Step 1: Test the configuration
        setIsTesting(true);
        try {
            const testRequest: LLMTestRequest = mode === 'add'
                ? { config: currentConfig as LLMProviderConfig, testPrompt: "Test connection" }
                : { providerId: _existingProvider!.id, testPrompt: "Test connection" };

            await llmSettingsApi.testProvider(testRequest);

        } catch (error) {
            setIsTesting(false);
            const errorMessage = error instanceof Error ? error.message : 'Configuration test failed';
            setTestError(errorMessage);
            return; // Don't proceed to save if test fails
        }

        // Step 2: If test passes, save the configuration
        setIsTesting(false);
        setIsSaving(true);
        try {
            await onSave(finalConfig);
            onClose();
        } catch (error) {
            // Error will be handled by parent component
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Dialog
                open={isOpen}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        handleClose();
                    }
                }}
            >
                <DialogSurface data-testid="llm-provider-modal">
                    <DialogBody>
                        <DialogTitle
                            action={
                                <Button
                                    appearance="subtle"
                                    aria-label="Close"
                                    icon={<Dismiss24Regular />}
                                    onClick={handleClose}
                                    disabled={isSaving || isTesting}
                                />
                            }
                        >
                            {mode === 'add' ? 'Configure New Provider' : 'Edit Provider Configuration'}
                        </DialogTitle>

                        <DialogContent>
                            {/* Display test error message if testing failed */}
                            {testError && (
                                <Alert
                                    intent="error"
                                    title="Configuration Test Failed"
                                    data-testid="test-error-message"
                                >
                                    {testError}
                                </Alert>
                            )}

                            <LLMProviderForm
                                mode={mode}
                                initialConfig={existingConfig}
                                onValidationChange={handleFormValidationChange}
                                onChange={handleFormChange}
                                disabled={isSaving || isTesting}
                                testIdPrefix="modal"
                                includeApiKey={isDuplicating}
                            />
                        </DialogContent>

                        <DialogActions>
                            <Button
                                data-testid="modal-cancel-button"
                                appearance="secondary"
                                onClick={handleClose}
                                disabled={isSaving || isTesting}
                            >
                                Cancel
                            </Button>
                            <AsyncButton
                                data-testid="modal-save-button"
                                appearance="primary"
                                onClick={handleSave}
                                disabled={!isValid || isSaving || isTesting}
                                loading={isTesting || isSaving}
                                loadingText={isTesting ? 'Testing...' : 'Saving...'}
                            >
                                Save
                            </AsyncButton>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            {/* Confirmation dialog for unsaved changes */}
            <Dialog open={showConfirmDialog}>
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Discard unsaved changes?</DialogTitle>
                        <DialogContent>
                            You have unsaved changes. Are you sure you want to close without saving?
                        </DialogContent>
                        <DialogActions>
                            <Button
                                data-testid="cancel-discard-button"
                                appearance="secondary"
                                onClick={handleCancelClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                data-testid="confirm-discard-button"
                                appearance="primary"
                                onClick={handleConfirmClose}
                            >
                                Discard
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};

export default LLMProviderModal;